import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// MUI
import {
    Alert, Avatar, Badge, Box, Button, Chip, CircularProgress, Container,
    Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControl, FormHelperText, Grid, IconButton, InputLabel,
    LinearProgress, MenuItem, Paper, Select, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Typography
} from '@mui/material';
import { AccountCircle, Add, CameraAlt, Delete, Edit, ManageAccounts } from '@mui/icons-material';
import axios from "axios";

const api = process.env.REACT_APP_API;

export default function Franchises({ snackbar, apiadmin }) {

    const navi = useNavigate();
    const [action, setAction] = useState('');
    const [open, setOpen] = useState(false);
    const [franchiseid, setFranchiseid] = useState('');

    // ── Franchise Login Account Dialog ──────────────────────────
    const [accountOpen, setAccountOpen] = useState(false);
    const [selectedFranchise, setSelectedFranchise] = useState(null);
    const [flLogins, setFlLogins] = useState([]);
    const [flLoading, setFlLoading] = useState(false);
    const [addingLogin, setAddingLogin] = useState(false);

    // Delete FL alert dialog
    const [flDeleteOpen, setFlDeleteOpen] = useState(false);
    const [flDeleteId, setFlDeleteId] = useState('');
    const [flDeleting, setFlDeleting] = useState(false);

    // ── Fetch franchise logins ───────────────────────────────────
    const fetchFranchiseLogins = (fid) => {
        setFlLoading(true);
        axios.get(`${api}/admin/franchise/login/getall?franchiseid=${fid}`)
            .then(res => {
                setFlLoading(false);
                setFlLogins(res.data.status ? res.data.data : []);
            })
            .catch(() => { setFlLoading(false); setFlLogins([]); });
    };

    const open_account = (franchise) => {
        setSelectedFranchise(franchise);
        setAccountOpen(true);
        fetchFranchiseLogins(franchise.franchiseid);
    };

    const close_account = () => {
        setAccountOpen(false);
        setSelectedFranchise(null);
        setFlLogins([]);
        formik.resetForm();
    };

    // ── Formik + Yup – Add login ──────────────────────────────────
    const formik = useFormik({
        initialValues: { fl_name: '', fl_email: '', fl_role: '', fl_status: '' },
        validationSchema: Yup.object({
            fl_name: Yup.string().min(2, 'Min 2 characters').required('Name is required'),
            fl_email: Yup.string().email('Invalid email').required('Email is required'),
            fl_role: Yup.string().oneOf(['admin', 'teacher', 'staff'], 'Select a valid role').required('Role is required'),
            fl_status: Yup.string().oneOf(['active', 'inactive'], 'Select a valid status').required('Status is required'),
        }),
        onSubmit: (values, { resetForm }) => {
            setAddingLogin(true);
            axios.post(`${api}/admin/franchise/login/add`, {
                ...values,
                franchiseid: selectedFranchise?.franchiseid,
            })
                .then(res => {
                    setAddingLogin(false);
                    snackbar(res.data.message || res.data.msg || 'Done');
                    if (res.data.status) {
                        resetForm();
                        fetchFranchiseLogins(selectedFranchise?.franchiseid);
                    }
                })
                .catch(() => { setAddingLogin(false); snackbar('Request failed'); });
        },
    });

    // ── Delete franchise login ────────────────────────────────────
    const open_fl_delete = (fl_id) => {
        setFlDeleteId(fl_id);
        setFlDeleteOpen(true);
    };

    const delete_fl = () => {
        setFlDeleting(true);
        axios.post(`${api}/admin/franchise/login/delete`, { fl_id: flDeleteId })
            .then(res => {
                setFlDeleting(false);
                setFlDeleteOpen(false);
                snackbar(res.data.message || res.data.msg || 'Done');
                if (res.data.status) {
                    fetchFranchiseLogins(selectedFranchise?.franchiseid);
                }
            })
            .catch(() => { setFlDeleting(false); snackbar('Request failed'); });
    };

    // ── Franchise list ────────────────────────────────────────────
    useEffect(() => { getFranchises(); }, []);

    const [franchises, setFranchises] = useState([]);
    const [fLoading, setFloading] = useState(false);

    const getFranchises = () => {
        setFloading(true);
        var formData = new FormData();
        formData.append('get-franchises', '');
        fetch(apiadmin, { method: 'post', body: formData })
            .then(res => res.json())
            .then(data => {
                setFloading(false);
                data.status == false ? setFranchises(data) : setFranchises(data.data);
            });
    };

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    const add_logo = (fid, event) => {
        snackbar('Uploading...');
        setAction('uploadinglogo');
        var formData = new FormData();
        formData.append('add-franchise-logo', fid);
        formData.append('image', event.target.files[0]);
        axios.post(apiadmin, formData).then(res => {
            setAction('');
            snackbar(res.data.msg);
            if (res.data.status === true) window.location.reload();
        });
    };

    const open_delete = (fid) => { setFranchiseid(fid); setOpen(true); };

    const delete_franchise = () => {
        setAction('deleting');
        var formData = new FormData();
        formData.append('delete-franchise', franchiseid);
        axios.post(apiadmin, formData).then(res => {
            setAction(''); setOpen(false);
            snackbar(res.data.msg);
            if (res.data.status === true) getFranchises();
        });
    };

    return (
        <Box className='content' mt={4}>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                    <Typography fontSize={14} fontWeight='bold'>Franchises</Typography>
                    <Box>
                        <Button startIcon={<Add />} variant="outlined" onClick={() => navi('/controller/franchise-upload/0')}>Add franchise</Button>
                    </Box>
                </Stack>

                {fLoading ? <LinearProgress color="primary" /> :
                    franchises.status == false ? <Alert severity="warning">{franchises.message}</Alert> :
                        <Grid container spacing={2}>
                            {franchises.map((data) =>
                                <Grid item xs={12} md={4} key={data.franchiseid}>
                                    <Box padding={1.5} component={Paper}>
                                        <Stack mb={3} direction='row' alignItems='center' spacing={2}>
                                            <Box>
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    badgeContent={
                                                        <IconButton component='label' disabled={action === 'uploadinglogo'} onChange={(e) => add_logo(data.franchiseid, e)}>
                                                            <CameraAlt />
                                                            <VisuallyHiddenInput type="file" accept=".jpeg,.jpg,.png" />
                                                        </IconButton>
                                                    }>
                                                    <Avatar alt={data.fr_name} sx={{ width: 120, height: 120, borderRadius: 5 }} src={`/spywareassets/franchise/${data.franchiseid}.jpg`} />
                                                </Badge>
                                            </Box>
                                            <Box>
                                                <Typography fontWeight='bold' fontSize={15} className="wrap-1">{data.fr_name}</Typography>
                                                <Typography fontSize={14} color='gray' className="wrap-1">{data.fr_address}, {data.fr_city}</Typography>
                                            </Box>
                                        </Stack>
                                        <Box mb={2}>
                                            <Typography noWrap fontWeight='bold' fontSize={14}>Owned by: {data.fr_owner_name}</Typography>
                                            <Typography noWrap fontWeight='bold' fontSize={14}>Email: {data.fr_email}</Typography>
                                            <Typography noWrap fontWeight='bold' fontSize={14}>Join date - {data.fr_joined}</Typography>
                                        </Box>
                                        <Divider />
                                        <Stack mt={1} direction='row' alignItems='center' justifyContent='space-between'>
                                            <Box>
                                                <IconButton color="error" onClick={() => open_delete(data.franchiseid)}><Delete /></IconButton>
                                                <IconButton color="warning" title="Manage Accounts" onClick={() => open_account(data)}><ManageAccounts /></IconButton>
                                                <IconButton color="primary" onClick={() => navi(`/controller/franchise-upload/${data.franchiseid}`)}><Edit /></IconButton>
                                            </Box>
                                            {data.fr_status == 'active'
                                                ? <Typography color='green' fontSize={14} fontWeight='bold'>Active</Typography>
                                                : <Typography color='orange' fontSize={14} fontWeight='bold'>Pending</Typography>}
                                        </Stack>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>}
            </Container>

            {/* ── Delete Franchise Dialog ─────────────────────────────── */}
            <Dialog open={open} fullWidth maxWidth='xs' onClose={() => setOpen(false)}>
                <DialogContent>
                    <Typography fontWeight='bold' fontSize={25} color='darkorange'>Do you want to delete this franchise?</Typography>
                    <Typography fontSize={14} color='gray'>Remember, this process will delete all data related to this franchise. Like student.</Typography>
                    <Typography fontSize={14} color='gray' mt={2}>To delete this franchise type '<i>delete</i>' below.</Typography>
                    <TextField
                        fullWidth variant="outlined" type="text" size="small"
                        placeholder="type 'delete' here" value={action}
                        onChange={(e) => setAction(e.target.value)}
                        onKeyUp={(e) => e.key === 'Enter' && action === 'delete' && delete_franchise()}
                    />
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button variant="outlined" onClick={() => setOpen(false)}>Close</Button>
                    <Button variant="contained" color="error"
                        disabled={action !== 'delete' || action === 'deleting'}
                        onClick={delete_franchise}>
                        {action === 'deleting' ? <CircularProgress color="inherit" size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Franchise Login Accounts Dialog ────────────────────── */}
            <Dialog open={accountOpen} fullWidth maxWidth='md' onClose={close_account}>
                <DialogTitle>
                    <Stack direction='row' alignItems='center' spacing={1}>

                        <Box>
                            <Typography fontWeight='bold' fontSize={17}>Franchise Login Accounts</Typography>
                            {selectedFranchise && (
                                <Typography fontSize={13} color='gray'>
                                    {selectedFranchise.fr_name} — {selectedFranchise.franchiseid}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent>

                    {/* Add Login Form */}
                    <Typography fontWeight='bold' fontSize={14} mb={1.5}>Add New Login</Typography>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>

                            {/* fl_name */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth size="small" label="Full Name"
                                    id="fl_name" name="fl_name"
                                    value={formik.values.fl_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.fl_name && Boolean(formik.errors.fl_name)}
                                    helperText={formik.touched.fl_name && formik.errors.fl_name}
                                />
                            </Grid>

                            {/* fl_email */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth size="small" label="Email" type="email"
                                    id="fl_email" name="fl_email"
                                    value={formik.values.fl_email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.fl_email && Boolean(formik.errors.fl_email)}
                                    helperText={formik.touched.fl_email && formik.errors.fl_email}
                                />
                            </Grid>

                            {/* fl_role */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small"
                                    error={formik.touched.fl_role && Boolean(formik.errors.fl_role)}>
                                    <InputLabel id="fl_role_label">Role</InputLabel>
                                    <Select
                                        labelId="fl_role_label"
                                        id="fl_role" name="fl_role"
                                        label="Role"
                                        value={formik.values.fl_role}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <MenuItem value="admin">Admin</MenuItem>
                                        <MenuItem value="teacher">Teacher</MenuItem>
                                        <MenuItem value="staff">Staff</MenuItem>
                                    </Select>
                                    {formik.touched.fl_role && formik.errors.fl_role &&
                                        <FormHelperText>{formik.errors.fl_role}</FormHelperText>}
                                </FormControl>
                            </Grid>

                            {/* fl_status */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small"
                                    error={formik.touched.fl_status && Boolean(formik.errors.fl_status)}>
                                    <InputLabel id="fl_status_label">Status</InputLabel>
                                    <Select
                                        labelId="fl_status_label"
                                        id="fl_status" name="fl_status"
                                        label="Status"
                                        value={formik.values.fl_status}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                    {formik.touched.fl_status && formik.errors.fl_status &&
                                        <FormHelperText>{formik.errors.fl_status}</FormHelperText>}
                                </FormControl>
                            </Grid>

                            {/* Submit */}
                            <Grid item xs={12}>
                                <Button type="submit" variant="contained"
                                    startIcon={addingLogin ? <CircularProgress size={18} color="inherit" /> : <Add />}
                                    disabled={addingLogin}>
                                    {addingLogin ? 'Adding...' : 'Add Login'}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>

                    <Divider sx={{ my: 2.5 }} />

                    {/* Login List */}
                    <Typography fontWeight='bold' fontSize={14} mb={1.5}>Login Accounts</Typography>
                    {flLoading ? <LinearProgress /> :
                        flLogins.length === 0 ? <Alert severity="info">No login accounts found.</Alert> :
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ background: '#f5f5f5' }}>
                                            <TableCell><Typography fontSize={13} fontWeight='bold'>Name</Typography></TableCell>
                                            <TableCell><Typography fontSize={13} fontWeight='bold'>Email</Typography></TableCell>
                                            <TableCell><Typography fontSize={13} fontWeight='bold'>Role</Typography></TableCell>
                                            <TableCell><Typography fontSize={13} fontWeight='bold'>Status</Typography></TableCell>
                                            <TableCell><Typography fontSize={13} fontWeight='bold'>Created At</Typography></TableCell>
                                            <TableCell align="center"><Typography fontSize={13} fontWeight='bold'>Action</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {flLogins.map((row) => (
                                            <TableRow key={row.fl_id} hover>
                                                <TableCell><Typography fontSize={13}>{row.fl_name}</Typography></TableCell>
                                                <TableCell><Typography fontSize={13}>{row.fl_email}</Typography></TableCell>
                                                <TableCell><Typography fontSize={13}>{row.fl_role}</Typography></TableCell>
                                                <TableCell>
                                                    <Chip label={row.fl_status} size="small"
                                                        color={row.fl_status === 'active' ? 'success' : 'warning'}
                                                        variant="outlined" />
                                                </TableCell>
                                                <TableCell><Typography fontSize={13}>{row.fl_created_at}</Typography></TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" color="error" onClick={() => open_fl_delete(row.fl_id)}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>}
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={close_account}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* ── Delete FL Account – Alert Dialog ───────────────────── */}
            <Dialog open={flDeleteOpen} maxWidth='xs' fullWidth onClose={() => !flDeleting && setFlDeleteOpen(false)}>
                <DialogContent>
                    <Typography fontWeight='bold' fontSize={20} color='error' mb={1}>Delete Login Account?</Typography>
                    <Typography fontSize={14} color='gray'>
                        Are you sure you want to delete this login account? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button variant="outlined" onClick={() => setFlDeleteOpen(false)} disabled={flDeleting}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={delete_fl} disabled={flDeleting}
                        startIcon={flDeleting ? <CircularProgress size={18} color="inherit" /> : <Delete />}>
                        {flDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}